import type { MissionEventSource, MissionRuntimeEvent, MissionRuntimeObserver } from '../missions';

/**
 * The runtime's event stream, made subscribable by a scene that arrives late.
 *
 * A mechanic scene is created *because* its stage was entered, and Phaser boots a scene on a
 * later tick, so by the time `create()` runs the scene has already missed the `STAGE_ENTERED`
 * that caused it and usually the first `ROUND_PRESENTED` of that stage too. A plain fan-out
 * would leave every scene opening on a blank screen until the child did something.
 *
 * So the relay keeps the few events that describe a *situation* rather than a moment, and
 * replays them to each new subscriber in the order they happened:
 *
 *   - `STAGE_ENTERED` — which stage is on screen.
 *   - `ROUND_PRESENTED` — the open round, dropped once it resolves so a scene cannot arrive to
 *     find a question that has already been answered.
 *   - `SCORE_CHANGED` — the current shard count.
 *   - `TEACHING_STARTED` — a teaching moment that is *still running*, dropped on `TEACHING_ENDED`
 *     exactly as the open round is dropped when it resolves. The mission's one scripted moment is
 *     delivered immediately after the stage is entered, so its scene is still booting when it
 *     begins; without this, the moment a child most needs to see was never drawn (R1-2).
 *   - `SHARED_MOMENT_PRESENTED` — an open both-required celebration, dropped on
 *     `SHARED_MOMENT_COMPLETED` exactly as the open round is dropped when it resolves. The
 *     victory scene boots into the moment it is supposed to be inviting children into (COOP-06).
 *   - `MISSION_COMPLETED` — the victory scene is created after the mission has finished, which
 *     is exactly when its celebration is supposed to play.
 *
 * Everything else (`ROUND_ASSIST`, `ROUND_RESOLVED`, a finished teaching moment, pause) is a
 * moment and is delivered live only. Replaying a resolved round's celebration into a scene that
 * opened afterwards would be a lie about what just happened.
 */

export interface MissionEventRelay extends MissionEventSource {
  /** Feed from the runtime. Safe to call after `dispose`; it simply goes nowhere. */
  publish(event: MissionRuntimeEvent): void;
  dispose(): void;
}

type StickyKey =
  | 'STAGE_ENTERED'
  | 'ROUND_PRESENTED'
  | 'SCORE_CHANGED'
  | 'TEACHING_STARTED'
  | 'SHARED_MOMENT_PRESENTED'
  | 'MISSION_COMPLETED';

/**
 * Replay order, so a late scene sees the stage before the round that is inside it — and the
 * teaching moment after the round, because that is the order it is drawn in: the moment covers
 * the question it is explaining.
 */
const REPLAY_ORDER: readonly StickyKey[] = [
  'STAGE_ENTERED',
  'SCORE_CHANGED',
  'ROUND_PRESENTED',
  'TEACHING_STARTED',
  'SHARED_MOMENT_PRESENTED',
  'MISSION_COMPLETED',
];

export function createMissionEventRelay(): MissionEventRelay {
  const observers = new Set<MissionRuntimeObserver>();
  const sticky = new Map<StickyKey, MissionRuntimeEvent>();
  let disposed = false;

  return {
    subscribe(observer: MissionRuntimeObserver): () => void {
      if (disposed) return () => {};
      observers.add(observer);

      for (const key of REPLAY_ORDER) {
        const event = sticky.get(key);
        // One listener throwing must not stop the rest of the replay reaching it.
        if (event !== undefined) safely(observer, event);
      }

      return () => {
        observers.delete(observer);
      };
    },

    publish(event: MissionRuntimeEvent): void {
      if (disposed) return;

      switch (event.type) {
        case 'STAGE_ENTERED':
          sticky.set('STAGE_ENTERED', event);
          // A new stage means the previous stage's round — and anything that was being taught
          // inside it — is over.
          sticky.delete('ROUND_PRESENTED');
          sticky.delete('TEACHING_STARTED');
          sticky.delete('SHARED_MOMENT_PRESENTED');
          break;
        case 'ROUND_PRESENTED':
          sticky.set('ROUND_PRESENTED', event);
          break;
        case 'ROUND_RESOLVED':
          sticky.delete('ROUND_PRESENTED');
          break;
        case 'SCORE_CHANGED':
          sticky.set('SCORE_CHANGED', event);
          break;
        case 'TEACHING_STARTED':
          sticky.set('TEACHING_STARTED', event);
          break;
        case 'TEACHING_ENDED':
          // Open-state rule, same as the round's: the moment is only a situation while it runs.
          sticky.delete('TEACHING_STARTED');
          break;
        case 'SHARED_MOMENT_PRESENTED':
          // The victory scene is booting while its celebration is being announced, which is
          // precisely when a moment a child is asked to join in must not be invisible (COOP-06).
          sticky.set('SHARED_MOMENT_PRESENTED', event);
          break;
        case 'SHARED_MOMENT_COMPLETED':
          // Open-state rule again: an announcement is a situation only while it is still open.
          sticky.delete('SHARED_MOMENT_PRESENTED');
          break;
        case 'MISSION_COMPLETED':
          sticky.set('MISSION_COMPLETED', event);
          break;
        default:
          break;
      }

      // Snapshot: a scene that unsubscribes while being notified must not skip its neighbour.
      for (const observer of [...observers]) {
        if (!observers.has(observer)) continue;
        safely(observer, event);
      }
    },

    dispose(): void {
      disposed = true;
      observers.clear();
      sticky.clear();
    },
  };
}

/**
 * A drawing failure in one scene must not stop the runtime.
 *
 * The relay is called from inside the mission runtime's own `onEvent`; letting a scene's
 * exception escape would abort a round in progress, which is a far worse outcome for the child
 * than one panel not redrawing.
 */
function safely(observer: MissionRuntimeObserver, event: MissionRuntimeEvent): void {
  try {
    observer(event);
  } catch {
    // Deliberately swallowed: there is no channel to report it on that is not this one.
  }
}
